import { useTranslation } from 'react-i18next'
import { UserTabProps } from './types'
import { VoltieUserDevice } from '../../types/VoltieUser'
import { Card, CardTitle, GroupLabel, DetailLabel, MutedValue, Badge, KeyValueRow, DeviceRow, DeviceName, DeviceMeta } from './userStyles'
import { maskToken, formatDateTime, fcmTokenInfo } from './userHelpers'

// Normalize the legacy PascalCase device keys.
function dev(d: VoltieUserDevice) {
  return {
    model: d.model || d.Model || '',
    manufacturer: d.manufacturer || d.Manufacturer || '',
    os: d.os || '',
    osVersion: d.os_version || d.OSVersion || '',
    appVersion: d.app_version || d.AppVersion || '',
    carrier: d.carrier || d.Carrier || '',
    network: d.network || '',
    lastLogin: d.last_login_date || '',
  }
}

function platformOf(os: string): 'Apple' | 'Android' | 'Other' {
  const o = os.toLowerCase()
  if (/ios|iphone|ipad|watch|mac/.test(o)) return 'Apple'
  if (/android/.test(o)) return 'Android'
  return 'Other'
}

const PLATFORM_LOGIN_ROWS: { key: string; label: string; labelKey?: string }[] = [
  { key: 'iPhone', label: 'iPhone' },
  { key: 'iPad', label: 'iPad' },
  { key: 'Android_phone', label: '', labelKey: 'user_platformAndroidPhone' },
  { key: 'Android_tablet', label: '', labelKey: 'user_platformAndroidTablet' },
  { key: 'frontend', label: '', labelKey: 'user_platformWeb' },
]

export default function UserDevices({ user }: UserTabProps) {
  const { t } = useTranslation()

  const devices = user.devices || {}
  const deviceIds = Object.keys(devices)
  const groups: { [k: string]: string[] } = { Apple: [], Android: [], Other: [] }
  for (const id of deviceIds) groups[platformOf(devices[id].os || '')].push(id)

  const liveActivities = Object.values(user.live_activity || {})
  const fcm = user.fcm_tokens || {}
  const fcmIds = Object.keys(fcm)
  const platformLogins = user.platform_logins || {}

  return (
    <div style={{ maxWidth: '820px' }}>
      {/* Devices grouped by platform */}
      <Card>
        <CardTitle>{t('user_devicesTitle')}</CardTitle>
        {deviceIds.length === 0 && <MutedValue>{t('user_noDevices')}</MutedValue>}
        {(['Apple', 'Android', 'Other'] as const).map(platform => groups[platform].length > 0 && (
          <div key={platform}>
            <GroupLabel>{platform === 'Other' ? t('user_otherDevices') : platform}</GroupLabel>
            {groups[platform].map(id => {
              const d = dev(devices[id])
              return (
                <DeviceRow key={id}>
                  <DeviceName>{d.model || id}{d.manufacturer ? ` · ${d.manufacturer}` : ''}</DeviceName>
                  <DeviceMeta>
                    <span>{t('user_latestAppVersion')}: {d.appVersion || '-'}</span>
                    <span>{t('user_lastModified')}: {formatDateTime(d.lastLogin)}</span>
                    {d.osVersion && <span>{t('user_os')}: {d.osVersion}</span>}
                    {d.carrier && <span>{d.carrier}</span>}
                    {d.network && <span>{d.network}</span>}
                  </DeviceMeta>
                </DeviceRow>
              )
            })}
          </div>
        ))}
      </Card>

      {/* Push tokens & platform logins */}
      <Card>
        <CardTitle>{t('user_pushTokensTitle')}</CardTitle>

        {liveActivities.length > 0 && (
          <div style={{ marginBottom: '16px' }}>
            <Badge $tone="primary">{t('user_liveActivityActive')} — {liveActivities.length} {t('user_session')}</Badge>
            {liveActivities.map((la, i) => la.charger_id && (
              <DeviceMeta key={i} style={{ marginTop: '4px' }}>{t('user_primaryCharger')}: {la.charger_id}</DeviceMeta>
            ))}
          </div>
        )}

        <GroupLabel>{t('user_platformLogins')}</GroupLabel>
        {PLATFORM_LOGIN_ROWS.map(row => (
          <KeyValueRow key={row.key}>
            <span>{row.labelKey ? t(row.labelKey) : row.label}</span>
            <span>{platformLogins[row.key] ? formatDateTime(platformLogins[row.key]) : <MutedValue>{t('user_noLogin')}</MutedValue>}</span>
          </KeyValueRow>
        ))}

        <GroupLabel>{t('user_pushTokens')} · {fcmIds.length} {t('user_pcs')}</GroupLabel>
        {fcmIds.length === 0 && <MutedValue>-</MutedValue>}
        {fcmIds.map(id => {
          const info = fcmTokenInfo(fcm[id])
          return (
            <KeyValueRow key={id}>
              <span>{devices[id] ? dev(devices[id]).model || id : (info.platform || id)}</span>
              <span style={{ fontFamily: 'monospace' }}>{maskToken(info.token)}</span>
            </KeyValueRow>
          )
        })}
        <div style={{ marginTop: '8px' }}>
          <DetailLabel>{t('user_tokenMaskedNote')}</DetailLabel>
        </div>
      </Card>
    </div>
  )
}
